import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router';
import { Redirect } from 'react-router-dom';
import { DeleteConfirmation, ExportDialog } from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import {
  useAllocationFilters,
  useAuth,
  useClientFilters,
  useDocumentTitle,
  useMemberFilters,
  useProjectFilters,
  useResourceFilters,
  useSearchParams,
  useSearchParamsConfig,
} from '~/hooks';
import projectStatuses from '~/lookups/project-statuses.js';
import { PageLoader } from '~/routes/public/pages';
import { dateFormats, mimeTypes, sortAlphabetically } from '~/utils';
import ActionBar from '../components/action-bar/ActionBar';
import { Allocations, Grid, Schedule, Sidebar, useGroups, useSchedule } from '../components/schedule';
import AllocationDetails from '../dialogs/AllocationDetails';
import AllocationForm from '../dialogs/AllocationForm';
import Cells from './cells/Cells';
import Filters from './filters/Filters';
import FiltersBar from './filters/FiltersBar';
import useClientMetrics from './hooks/useClientMetrics';
import useHeaderMetrics from './hooks/useHeaderMetrics';
import useProjectMetrics from './hooks/useProjectMetrics';
import Empty from './sidebar/Empty';
import Group from './sidebar/Group';
import HeaderLabel from './sidebar/HeaderLabel';
import Row from './sidebar/Row';
import ReassignAllocationsConfirmation from '../components/ReassignAllocationsConfirmation';
import SetBookingTypeConfirmation from '../components/SetBookingTypeConfirmation';
import ShiftAllocationsConfirmation from '../components/ShiftAllocationsConfirmation';

const periods = {
  day: 5,
  week: 11,
  month: 11,
};

const merge = (source, target, key) => {
  const sourceObject = _.keyBy(source, key);
  const targetObject = _.keyBy(target, key);
  const merged = _.merge(sourceObject, targetObject);
  const result = _.values(merged);
  return result;
};

export default function ProjectSchedule() {
  const documentTitle = useDocumentTitle('Resource Schedule');
  const route = useRouteMatch();
  const history = useHistory();
  const api = useApi();
  const { workspace } = useWorkspace();
  const toast = useToast();
  const auth = useAuth();
  const confirmation = useConfirmation();

  const [query, setQuery] = useState({
    status: 'loading',
    action: 'load',
    fetching: false,
    projects: [],
    resources: [],
    allocations: [],
    metrics: {
      total: [],
      clients: [],
      projects: [],
    },
  });

  const location = useLocation();

  const searchParamsConfig = useSearchParamsConfig();

  const allocationFilters = useAllocationFilters();
  const clientFilters = useClientFilters();
  const projectFilters = useProjectFilters({
    projectRecordStatusId: { default: 'active', ...searchParamsConfig.projectRecordStatusId },
    projectStatuses: {
      default: [projectStatuses.in_progress, projectStatuses.not_started],
      ...searchParamsConfig.projectStatuses,
    },
  });
  const memberFilters = useMemberFilters();
  const resourceFilters = useResourceFilters();

  const [params, setParams] = useState(() => ({
    date: moment().format(dateFormats.isoDate),
    unit: 'week',
    onlyAllocatedProjects: false,
    groups: null,

    ...allocationFilters.filters,
    ...clientFilters.filters,
    ...projectFilters.filters,
    ...memberFilters.filters,
    ...resourceFilters.filters,
  }));

  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        date: {
          default: moment().format(dateFormats.isoDate),
          deserialize: (value) => (moment(value).isValid() ? moment(value).format(dateFormats.isoDate) : null),
        },
        unit: { default: 'week', valid: ['day', 'week', 'month'] },
        onlyAllocatedProjects: {
          default: false,
          serialize: (value) => (value === true ? 'true' : 'false'),
          deserialize: (value) => value === 'true',
        },
        groups: { valid: ['expanded', 'collapsed'] },

        ...allocationFilters.searchParamsConfig,
        ...clientFilters.searchParamsConfig,
        ...projectFilters.searchParamsConfig,
        ...memberFilters.searchParamsConfig,
        ...resourceFilters.searchParamsConfig,
      }),
      [allocationFilters, clientFilters, projectFilters, memberFilters, resourceFilters],
    ),
    sessionKey: 'project_allocations',
    onChange: useCallback((params) => setParams((state) => ({ ...state, ...params })), []),
  });

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      if (params) {
        setParams((state) => ({ ...state, ...params }));
        setSearchParamsStatus('ready');
      }
    });
  }, [searchParams, searchParamsStatus]);

  const [allocationFormInitialValues, setAllocationFormInitialValues] = useState(() => {
    const unit = { day: 'isoWeek', week: 'isoWeek', month: 'month' }[params.unit];

    return {
      start: moment(params.date).startOf(unit).format(dateFormats.isoDate),
      end: moment(params.date).endOf(unit).format(dateFormats.isoDate),
    };
  });

  const { start, end } = useMemo(() => {
    let start;
    let end;

    switch (params.unit) {
      case 'day':
        start = moment(params.date).startOf('isoWeek').format(dateFormats.isoDate);
        end = moment(params.date).add(periods.day, 'weeks').endOf('isoWeek').format(dateFormats.isoDate);
        break;

      case 'week':
        start = moment(params.date).startOf('isoWeek').format(dateFormats.isoDate);
        end = moment(params.date).add(periods.week, 'weeks').endOf('isoWeek').format(dateFormats.isoDate);
        break;

      case 'month':
        start = moment(params.date).startOf('month').format(dateFormats.isoDate);
        end = moment(params.date).add(periods.month, 'months').endOf('month').format(dateFormats.isoDate);
        break;
    }

    return { start, end };
  }, [params.date, params.unit]);

  const [projectMembers, setProjectMembers] = useState({});

  // Rows emptied by a drag reassign this session, kept visible so allocations can be dragged back.
  const [emptiedRows, setEmptiedRows] = useState({});

  const handleShowTeam = useCallback(
    async (projectId) => {
      try {
        const { data } = await api.www
          .workspaces(workspace.id)
          .allocations()
          .projectSchedule.projectTeam({
            projectId,
            ...memberFilters.mapUrlSearchParams({
              members: params.members,
              memberBillableTypeId: params.memberBillableTypeId,
              memberCostMethods: params.memberCostMethods,
              memberDisciplines: params.memberDisciplines,
              memberEmploymentTypes: params.memberEmploymentTypes,
              memberInvitationStatuses: params.memberInvitationStatuses,
              memberJobTitles: params.memberJobTitles,
              memberLocations: params.memberLocations,
              memberManagers: params.memberManagers,
              memberPractices: params.memberPractices,
              memberSkills: params.memberSkills,
              memberStatusId: params.memberStatusId,
              memberTags: params.memberTags,
            }),
            ...resourceFilters.mapUrlSearchParams({
              resourceDisciplines: params.resourceDisciplines,
              resourceLocations: params.resourceLocations,
              resourcePractices: params.resourcePractices,
              resourceSkills: params.resourceSkills,
              resourceStatusId: params.resourceStatusId,
              resourceTypeId: params.resourceTypeId,
            }),
          });

        setProjectMembers((state) => ({ ...state, [projectId]: data }));
        return data;
      } catch (error) {
        toast.error('An error has occurred. Please try again.');
      }
    },
    [
      toast,
      api,
      workspace.id,
      memberFilters,
      resourceFilters,
      params.members,
      params.memberBillableTypeId,
      params.memberCostMethods,
      params.memberDisciplines,
      params.memberEmploymentTypes,
      params.memberInvitationStatuses,
      params.memberJobTitles,
      params.memberLocations,
      params.memberManagers,
      params.memberPractices,
      params.memberSkills,
      params.memberStatusId,
      params.memberTags,
      params.resourceDisciplines,
      params.resourceLocations,
      params.resourcePractices,
      params.resourceSkills,
      params.resourceStatusId,
      params.resourceTypeId,
    ],
  );

  const handleAssignAllocatedMembers = useCallback(
    async (projectId) => {
      try {
        await api.www.workspaces(workspace.id).allocations().projectSchedule.assignAllocatedMembers(projectId);

        toast.success('Project assignments have been created.');
        refetchAllocations();
      } catch (error) {
        toast.error('An error has occurred. Please try again.');
      }
    },
    [api.www, workspace.id, toast],
  );

  const handleShiftAllocations = useCallback(
    async (project) => {
      await confirmation.prompt((resolve) => (
        <ShiftAllocationsConfirmation
          project={project}
          onSubmit={async () => {
            refetchAllocations();
            resolve();
          }}
          onClose={resolve}
        />
      ));
    },
    [confirmation],
  );

  const handleReassignAllocations = useCallback(
    async (project, resource) => {
      await confirmation.prompt((resolve) => (
        <ReassignAllocationsConfirmation
          project={project}
          resource={resource}
          onSubmit={async () => {
            refetchAllocations();
            resolve();
          }}
          onClose={resolve}
        />
      ));
    },
    [confirmation],
  );

  const handleSetBookingType = useCallback(
    async (project, bookingTypeId) => {
      await confirmation.prompt((resolve) => (
        <SetBookingTypeConfirmation
          project={project}
          bookingTypeId={bookingTypeId}
          onSubmit={async () => {
            refetch();
            resolve();
          }}
          onClose={resolve}
        />
      ));
    },
    [confirmation],
  );

  const handleSetResourceBookingType = useCallback(
    async (project, resource, bookingTypeId) => {
      await confirmation.prompt((resolve) => (
        <SetBookingTypeConfirmation
          project={project}
          resource={resource}
          bookingTypeId={bookingTypeId}
          onSubmit={async () => {
            refetch();
            resolve();
          }}
          onClose={resolve}
        />
      ));
    },
    [confirmation],
  );

  const data = useMemo(() => {
    const allocations = query.allocations.filter(
      (allocation) => moment(allocation.start).isSameOrBefore(end) && moment(allocation.end).isSameOrAfter(start),
    );

    const resourcesById = _.keyBy(query.resources, 'id');
    const allocationsByProject = _.groupBy(allocations, 'projectId');

    let projects = query.projects;

    if (params.onlyAllocatedProjects) {
      projects = projects.filter((p) => allocations.some((a) => a.projectId === p.id));
    }

    const rows = [];

    const totalRow = {
      id: 'total',
      type: 'header',
      label: 'Total',
      showExpandCollapse: true,
      cells: [],
    };

    if (projects.length > 0) {
      rows.push(totalRow);
    }

    const metrics = {
      clients: _.groupBy(query.metrics.clients, (metric) => `${metric.clientId}`),
      projects: _.groupBy(query.metrics.projects, (metric) => `${metric.id}_${metric.start}`),
      total: _.groupBy(query.metrics.total, (metric) => metric.start),
    };

    const periodCount = moment(end).diff(start, params.unit) + 1;

    for (let index = 0; index < periodCount; index++) {
      const date = moment(start).add(index, params.unit).format(dateFormats.isoDate);

      const metric = metrics.total[date] ? metrics.total[date][0] : null;

      totalRow.cells.push({
        key: `total_${date}`,
        mode: 'header',
        type: 'total',
        date,
        allocated: metric?.allocated ?? 0,
      });
    }

    //Group the projects by client
    const projectsByClient = _.groupBy(projects, 'client.id');

    _.forEach(projectsByClient, (projects, key) => {
      const headerRow = {
        id: key,
        type: 'header',
        cells: [],
        client: projects[0].client,
      };

      let headerSubtotal = _.groupBy(metrics.clients[projects[0].client.id], 'start');

      for (let index = 0; index < periodCount; index++) {
        const date = moment(start).add(index, params.unit).format(dateFormats.isoDate);
        const metric = headerSubtotal[date] ? headerSubtotal[date][0] : null;

        headerRow.cells.push({
          key: `${key}_${date}`,
          mode: 'header',
          type: 'header',
          date,
          allocated: metric?.allocated ?? 0,
        });
      }
      rows.push(headerRow);

      for (const project of projects) {
        const group = {
          id: project.id,
          type: 'group',
          component: (props) => (
            <Group
              {...props}
              hasMembers={projectMembers[project.id]?.length > 0}
              onShowTeam={!projectMembers[project.id] ? handleShowTeam : undefined}
              onAssignAllocatedMembers={
                project.permissions.manageAllocations ? handleAssignAllocatedMembers : undefined
              }
              onShiftAllocations={project.permissions.manageAllocations ? handleShiftAllocations : undefined}
              onSetBookingType={project.permissions.manageAllocations ? handleSetBookingType : undefined}
            />
          ),
          projectId: project.id,
          project,
          hasAllocations: allocationsByProject[project.id]?.length > 0,
          cells: [],
        };
        rows.push(group);

        for (let index = 0; index < periodCount; index++) {
          const date = moment(start).add(index, params.unit).format(dateFormats.isoDate);

          const key = `${group.id}_${date}`;

          const metric = metrics.projects[key] ? metrics.projects[key][0] : {};

          group.cells.push({
            key: `${group.id}_${date}`,
            mode: 'standard',
            type: 'group',
            group,
            manage: project.permissions.manageAllocations,
            date,
            allocated: metric.allocated ?? 0,
          });
        }

        const projectResources = {};

        // Order the allocations by resource type and name
        const projectAllocations = sortAlphabetically(allocationsByProject[project.id] || [], [
          'resourceTypeId',
          (allocation) => resourcesById[allocation.resourceId]?.name,
        ]);

        for (const allocation of projectAllocations) {
          if (!projectResources[allocation.resourceId]) {
            projectResources[allocation.resourceId] = {
              id: `${project.id}_${allocation.resourceId}`,
              resourceId: allocation.resourceId,
              type: 'row',
              component: (props) => (
                <Row
                  {...props}
                  onReassignAllocations={handleReassignAllocations}
                  onSetBookingType={handleSetResourceBookingType}
                />
              ),
              projectId: project.id,
              project: project,
              parentId: group.id,
              resource: resourcesById[allocation.resourceId],
              allocations: [],
              cells: [],
            };
          }

          projectResources[allocation.resourceId].allocations.push(allocation);
        }

        if (projectMembers[project.id]) {
          for (const projectMember of projectMembers[project.id]) {
            if (!projectResources[projectMember.memberId]) {
              projectResources[projectMember.memberId] = {
                id: `${project.id}_${projectMember.memberId}`,
                resourceId: projectMember.memberId,
                type: 'row',
                component: (props) => <Row {...props} />,
                projectId: project.id,
                parentId: group.id,
                resource: projectMember.member,
                allocations: [],
                cells: [],
              };
            }
          }
        }

        // Keep rows that were emptied by a drag reassign visible until the next refresh.
        for (const { projectId, resource, showUnassignedWarning } of Object.values(emptiedRows)) {
          if (projectId !== project.id || !resource || projectResources[resource.id]) continue;

          projectResources[resource.id] = {
            id: `${project.id}_${resource.id}`,
            resourceId: resource.id,
            type: 'row',
            // No action handlers: an emptied row has no allocations to reassign or rebook.
            component: (props) => <Row {...props} />,
            projectId: project.id,
            project,
            parentId: group.id,
            resource,
            allocations: [],
            cells: [],
            showUnassignedWarning,
          };
        }

        sortAlphabetically(projectResources, 'resource.name').forEach((value) => {
          // Add cells for each assignment row
          for (let index = 0; index < periodCount; index++) {
            const date = moment(start).add(index, params.unit).format(dateFormats.isoDate);

            value.cells.push({
              key: `${value.id}_${date}`,
              mode: 'row',
              type: 'row',
              group,
              manage: project.permissions.manageAllocations,
              row: value,
              date,
            });
          }

          rows.push(value);
        });
      }
    });

    return { rows };
  }, [
    query.allocations,
    query.projects,
    query.resources,
    query.metrics,
    start,
    end,
    params.unit,
    params.onlyAllocatedProjects,
    projectMembers,
    emptiedRows,
    handleShowTeam,
    handleAssignAllocatedMembers,
    handleReassignAllocations,
    handleShiftAllocations,
    handleSetBookingType,
    handleSetResourceBookingType,
  ]);

  const updateParams = useCallback(
    (params) => {
      setParams((state) => ({ ...state, ...params }));
      searchParams.set(params);
    },
    [searchParams],
  );

  const handleGroupsToggle = useCallback(
    (status) => {
      updateParams({ groups: status });
    },
    [updateParams],
  );

  const { groups, hasExpandedGroups, toggleGroup, toggleGroups } = useGroups({
    rows: data?.rows,
    defaultGroupStatus: params.groups,
    onGroupsToggle: handleGroupsToggle,
  });

  const canvasRef = useRef();
  const bodyRef = useRef();

  const {
    allocations,
    cells,
    styles,
    rows: dropRows,
    virtualRows: rows,
  } = useSchedule({
    data,
    start,
    end,
    unit: params.unit,
    groups,
    parentRef: bodyRef,
  });

  const headerMetrics = useHeaderMetrics({
    parentRef: bodyRef,
    headers: useMemo(() => rows.filter((row) => row.type === 'header'), [rows]),
    projects: useMemo(() => data.rows.filter((row) => row.type === 'group'), [data.rows]),
    start,
    end,
    unit: params.unit,
    allocations: query.allocations,
    onFetched: useCallback((metrics) => {
      setQuery((state) => {
        return state.metrics
          ? {
              ...state,
              metrics: {
                ...state.metrics,
                total: merge(state.metrics.total, metrics, (obj) => `${obj.start}`),
              },
            }
          : state;
      });
    }, []),
  });

  const clientMetrics = useClientMetrics({
    parentRef: bodyRef,
    clients: useMemo(() => rows.filter((row) => row.type === 'header'), [rows]),
    projects: useMemo(() => data.rows.filter((row) => row.type === 'group'), [data.rows]),
    start,
    end,
    unit: params.unit,
    allocations: query.allocations,
    onFetched: useCallback((metrics) => {
      setQuery((state) => {
        return state.metrics
          ? {
              ...state,
              metrics: {
                ...state.metrics,
                clients: merge(state.metrics.clients, metrics, (obj) => `${obj.clientId}_${obj.start}`),
              },
            }
          : state;
      });
    }, []),
  });

  const projectMetrics = useProjectMetrics({
    parentRef: bodyRef,
    projects: useMemo(() => rows.filter((row) => row.type === 'group'), [rows]),
    start,
    end,
    unit: params.unit,
    allocations: query.allocations,
    onFetched: useCallback((metrics) => {
      setQuery((state) => {
        return state.metrics
          ? {
              ...state,
              metrics: {
                ...state.metrics,
                projects: merge(state.metrics.projects, metrics, (obj) => `${obj.id}_${obj.start}`),
              },
            }
          : state;
      });
    }, []),
  });

  const fetchProjects = useCallback(async () => {
    setQuery((state) => ({ ...state, action: 'fetch-projects' }));

    const { data } = await api.www
      .workspaces(workspace.id)
      .allocations()
      .projectSchedule.projects({
        ...clientFilters.mapUrlSearchParams(params),
        ...projectFilters.mapUrlSearchParams(params),
      });

    setQuery((state) => ({ ...state, action: null, projects: data }));
    return data;
  }, [api, workspace.id, params, clientFilters, projectFilters]);

  const fetchResources = useCallback(async () => {
    setQuery((state) => ({ ...state, action: 'fetch-resources' }));

    const { data } = await api.www
      .workspaces(workspace.id)
      .allocations()
      .projectSchedule.resources({
        ...resourceFilters.mapUrlSearchParams(params),
        ...memberFilters.mapUrlSearchParams(params),
      });

    setQuery((state) => ({ ...state, action: null, resources: data }));
    return data;
  }, [api, workspace.id, params, resourceFilters, memberFilters]);

  const fetchAllocations = useCallback(async () => {
    setQuery((state) => ({ ...state, fetching: true, action: 'fetch-allocations' }));

    const { data } = await api.www
      .workspaces(workspace.id)
      .allocations()
      .projectSchedule.allocations({
        start,
        end,
        unit: params.unit,

        ...allocationFilters.mapUrlSearchParams(params),
        ...clientFilters.mapUrlSearchParams(params),
        ...projectFilters.mapUrlSearchParams(params),
        ...memberFilters.mapUrlSearchParams(params),
        ...resourceFilters.mapUrlSearchParams(params),
      });

    setQuery((state) => ({ ...state, fetching: false, action: null, allocations: data }));

    headerMetrics.initialize();
    clientMetrics.initialize();
    projectMetrics.initialize();

    return data;
  }, [
    start,
    end,
    params,
    api,
    workspace.id,
    headerMetrics,
    clientMetrics,
    projectMetrics,
    allocationFilters,
    clientFilters,
    projectFilters,
    memberFilters,
    resourceFilters,
  ]);

  const fetchMoreAllocations = useCallback(
    async ({ start, end }) => {
      setQuery((state) => ({ ...state, fetching: true }));

      const { data } = await api.www
        .workspaces(workspace.id)
        .allocations()
        .projectSchedule.allocations({
          start,
          end,
          unit: params.unit,

          ...allocationFilters.mapUrlSearchParams(params),
          ...clientFilters.mapUrlSearchParams(params),
          ...projectFilters.mapUrlSearchParams(params),
          ...memberFilters.mapUrlSearchParams(params),
          ...resourceFilters.mapUrlSearchParams(params),
        });

      setQuery((state) => ({
        ...state,
        status: 'ready',
        action: 'fetch-more',
        fetching: false,
        allocations: merge(state.allocations, data, 'id'),
      }));

      headerMetrics.load({ start, end, unit: params.unit, allocations: data });
      clientMetrics.load({ start, end, unit: params.unit, allocations: data });
      projectMetrics.load({ start, end, unit: params.unit, allocations: data });

      return data;
    },
    [
      params,
      api,
      workspace.id,
      headerMetrics,
      clientMetrics,
      projectMetrics,
      allocationFilters,
      clientFilters,
      projectFilters,
      memberFilters,
      resourceFilters,
    ],
  );

  const queryAction = query.action;

  useEffect(() => {
    if (searchParamsStatus !== 'ready' || !['load', 'refetch', 'refetch-allocations'].includes(queryAction)) return;

    (async () => {
      switch (queryAction) {
        case 'load':
        case 'refetch': {
          await fetchResources();
          await fetchProjects();
          await fetchAllocations();
          break;
        }

        case 'refetch-allocations':
          await fetchAllocations();
          break;
      }

      setQuery((state) => ({ ...state, fetching: false, action: null, status: 'ready' }));
    })();
  }, [searchParamsStatus, queryAction, fetchResources, fetchAllocations, fetchProjects]);

  const refetch = () => {
    setQuery((state) => ({
      ...state,
      action: 'refetch',
      metrics: {
        total: [],
        clients: [],
        projects: [],
      },
    }));
  };

  const refetchAllocations = () => {
    setQuery((state) => ({
      ...state,
      action: 'refetch-allocations',
      metrics: {
        total: [],
        clients: [],
        projects: [],
      },
    }));
  };

  const handleDateChange = (date) => {
    updateParams({ date });
    refetchAllocations();
    setEmptiedRows({});
  };

  const handleDateNavPrevious = () => {
    const date = {
      day: moment(params.date).subtract(1, 'week'),
      week: moment(params.date).subtract(1, 'week'),
      month: moment(params.date).subtract(1, 'month'),
    }[params.unit].format(dateFormats.isoDate);

    updateParams({ date });

    const { start, end } = {
      day: {
        start: moment(date).startOf('isoWeek').format(dateFormats.isoDate),
        end: moment(date).endOf('isoWeek').format(dateFormats.isoDate),
      },
      week: {
        start: moment(date).startOf('isoWeek').format(dateFormats.isoDate),
        end: moment(date).endOf('isoWeek').format(dateFormats.isoDate),
      },
      month: {
        start: moment(date).startOf('month').format(dateFormats.isoDate),
        end: moment(date).endOf('month').format(dateFormats.isoDate),
      },
    }[params.unit];

    fetchMoreAllocations({ start, end });
  };

  const handleDateNavNext = () => {
    const date = {
      day: moment(params.date).add(1, 'week'),
      week: moment(params.date).add(1, 'week'),
      month: moment(params.date).add(1, 'month'),
    }[params.unit].format(dateFormats.isoDate);

    updateParams({ date });

    const { start, end } = {
      day: {
        start: moment(date).add(periods.day, 'weeks').startOf('isoWeek').format(dateFormats.isoDate),
        end: moment(date).add(periods.day, 'weeks').endOf('isoWeek').format(dateFormats.isoDate),
      },
      week: {
        start: moment(date).add(periods.week, 'weeks').startOf('isoWeek').format(dateFormats.isoDate),
        end: moment(date).add(periods.week, 'weeks').endOf('isoWeek').format(dateFormats.isoDate),
      },
      month: {
        start: moment(date).add(periods.month, 'months').startOf('month').format(dateFormats.isoDate),
        end: moment(date).add(periods.month, 'months').endOf('month').format(dateFormats.isoDate),
      },
    }[params.unit];

    fetchMoreAllocations({ start, end });
  };

  const handleFormClose = () => {
    const unit = { day: 'isoWeek', week: 'isoWeek', month: 'month' }[params.unit];

    setAllocationFormInitialValues({
      start: moment(params.date).startOf(unit).format(dateFormats.isoDate),
      end: moment(params.date).endOf(unit).format(dateFormats.isoDate),
    });
    history.push({ pathname: route.url, search: location.search });
    documentTitle.set('Resource Schedule');
  };

  const handleCreateAllocation = () => {
    history.push({ pathname: route.url.concat('/new'), search: location.search });
  };

  const handleView = (allocation) => {
    history.push({ pathname: `${route.url}/view/${allocation.id}`, search: location.search });
  };

  const handleEdit = (allocation) => {
    history.push({ pathname: `${route.url}/edit/${allocation.id}`, search: location.search });
  };

  const handleClone = async (allocation) => {
    try {
      await api.www.workspaces(workspace.id).allocations(allocation.id).clone();
      await fetchAllocations();
      toast.success('Allocation successfully cloned.');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSplit = async (allocation, date) => {
    try {
      await api.www.workspaces(workspace.id).allocations(allocation.id).split({ date });
      await fetchAllocations();
      toast.success('Allocation successfully split.');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSplitByDay = async (allocation) => {
    try {
      await api.www.workspaces(workspace.id).allocations(allocation.id).splitByDay();
      await fetchAllocations();
      toast.success('Allocation successfully split by day.');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSplitByWeek = async (allocation) => {
    try {
      await api.www.workspaces(workspace.id).allocations(allocation.id).splitByWeek();
      await fetchAllocations();
      toast.success('Allocation successfully split by week.');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSplitByMonth = async (allocation) => {
    try {
      await api.www.workspaces(workspace.id).allocations(allocation.id).splitByMonth();
      await fetchAllocations();
      toast.success('Allocation successfully split by month.');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (allocation) => {
    const confirm = await confirmation.prompt((resolve) => (
      <DeleteConfirmation resolve={resolve}>Are you sure you want to delete this allocation?</DeleteConfirmation>
    ));

    if (!confirm) return;

    await api.www.workspaces(workspace.id).allocations(allocation.id).delete();

    await fetchAllocations();
  };

  const handleSelectCellsEnd = (cells) => {
    const unit = { day: 'day', week: 'isoWeek', month: 'month' }[params.unit];

    const cell = _.first(cells);
    const lastCell = _.last(cells);

    let initialValues = {
      start: moment(cell.date).startOf(unit).format(dateFormats.isoDate),
      end: moment(lastCell.date).endOf(unit).format(dateFormats.isoDate),
    };

    const project = query.projects.find((r) => r.id === cell.group.project.id);

    if (project) {
      initialValues.assignmentTypeId = 'project';
      initialValues.projectId = project.id;
    }

    if (cell.row) {
      const resources = _.unionWith(
        query.resources,
        _(projectMembers)
          .values()
          .flatten()
          .map((pm) => pm.member)
          .compact()
          .value(),
        'id',
      );

      const resource = resources.find((a) => a.id === cell.row.resource.id);

      if (resource) {
        const resourceType = { member: 'member', placeholder: 'placeholder' }[resource.resourceType];

        initialValues.resourceTypeId = resource.resourceType;
        initialValues[resourceType] = resource;
      }
    }

    setAllocationFormInitialValues(initialValues);
    history.push({ pathname: route.url.concat('/new'), search: location.search });
  };

  const handleUpdateAllocationDates = useCallback(
    async (allocation) => {
      try {
        const body = _.pick(allocation, [
          'unit',
          'start',
          'end',
          'hoursPerDay',
          'hoursPerWeek',
          'hoursPerMonth',
          'hoursPerAllocation',
          'hoursRatioOfCapacity',
        ]);

        // Optimistic update
        setQuery((state) => ({
          ...state,
          allocations: state.allocations.map((a) => (a.id === allocation.id ? { ...a, ...body } : a)),
        }));

        await api.www.workspaces(workspace.id).allocations(allocation.id).upsert(body);
      } catch (error) {
        toast.error(error.message);
      } finally {
        await fetchAllocations();
      }
    },
    [api, fetchAllocations, toast, workspace.id],
  );

  const handleAllocationReassign = useCallback(
    async (allocation, targetRow) => {
      const resource = targetRow?.resource;
      if (!resource) return;

      // Only the dates change on a diagonal drag; the API preserves the rest of the schedule.
      const dates = { start: allocation.start, end: allocation.end };

      const reassignment = {
        resourceTypeId: resource.resourceType,
        memberId: resource.resourceType === 'member' ? resource.id : null,
        resourcePlaceholderId: resource.resourceType === 'placeholder' ? resource.id : null,
      };

      const sourceResource = query.resources.find((r) => r.id === allocation.resourceId);
      if (sourceResource) {
        setEmptiedRows((state) => ({
          ...state,
          [`${allocation.projectId}_${allocation.resourceId}`]: {
            projectId: allocation.projectId,
            resource: sourceResource,
            showUnassignedWarning: allocation.isProjectMember === false,
          },
        }));
      }

      // Optimistic update; re-add the target if it's an emptied row, and clear its stale membership flag
      setQuery((state) => ({
        ...state,
        resources: state.resources.some((r) => r.id === resource.id) ? state.resources : [...state.resources, resource],
        allocations: state.allocations.map((a) =>
          a.id === allocation.id
            ? { ...a, ...dates, ...reassignment, resourceId: resource.id, isProjectMember: null }
            : a,
        ),
      }));

      try {
        // Send the new dates so a diagonal drag applies its date change in the same call.
        await api.www
          .workspaces(workspace.id)
          .allocations(allocation.id)
          .reassign({ ...reassignment, ...dates });
      } catch (error) {
        toast.error(error.message);
      } finally {
        await fetchAllocations();
      }
    },
    [api, fetchAllocations, toast, workspace.id, query.resources],
  );

  // Route a drop: landing on a different resource row is a reassignment, otherwise it's a date move.
  const handleAllocationDrop = useCallback(
    (allocation, targetRow) => {
      const resourceChanged = targetRow?.resource && targetRow.resourceId !== allocation.resourceId;
      return resourceChanged
        ? handleAllocationReassign(allocation, targetRow)
        : handleUpdateAllocationDates(allocation);
    },
    [handleAllocationReassign, handleUpdateAllocationDates],
  );

  const handleUnitChange = (unit) => {
    if (unit !== params.unit) {
      setQuery((state) => ({ ...state, status: 'grid_loading' }));
      updateParams({ unit });
      refetchAllocations();
      setEmptiedRows({});
    }
  };

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);
  const handleApplyFilters = (values) => {
    if (values !== params) {
      values = _.omit(values, 'unit', 'date');
      setQuery((state) => ({ ...state, status: 'filtering' }));
      updateParams({ ...values });
      refetch();
      setProjectMembers([]);
      setEmptiedRows({});
    }

    hideFilters();
  };

  const handleFilterChange = (filter) => {
    handleApplyFilters({ ...params, ...filter });
  };

  const handleAllocationSaved = async (allocation) => {
    await fetchAllocations();

    if (groups[allocation.assignmentId]?.state !== 'expanded') {
      toggleGroup({ id: allocation.assignmentId });
    }
  };

  const components = useMemo(
    () => ({
      sidebar: {
        Empty,
        Group,
        Row,
        HeaderLabel,
      },
    }),
    [],
  );

  const handleExport = async (mimeType) => {
    let filename =
      mimeType === mimeTypes.xlsx
        ? `project_allocations_by_${params.unit}.xlsx`
        : `project_allocations_by_${params.unit}.csv`;

    confirmation.prompt((resolve) => (
      <ExportDialog
        filename={filename}
        onLoad={api.www
          .workspaces(workspace.id)
          .allocations()
          .projectSchedule.export(
            {
              assignmentTypeId: ['project'],
              start,
              end,
              unit: params.unit,
              onlyAllocatedProjects: params.onlyAllocatedProjects ? 'true' : undefined,
              ...allocationFilters.mapUrlSearchParams(params),
              ...clientFilters.mapUrlSearchParams(params),
              ...projectFilters.mapUrlSearchParams(params),
              ...memberFilters.mapUrlSearchParams(params),
              ...resourceFilters.mapUrlSearchParams(params),
            },
            {
              headers: { accept: mimeType },
              responseType: 'blob',
            },
          )}
        onClose={resolve}
      />
    ));
  };

  if (query.status === 'loading') return <PageLoader />;

  return (
    <>
      <ActionBar
        unit={params.unit}
        date={params.date}
        start={start}
        end={end}
        filters={true}
        create={true}
        disabled={query.fetching}
        onCreateAllocation={handleCreateAllocation}
        onDateChange={handleDateChange}
        onDateNavNext={handleDateNavNext}
        onDateNavPrevious={handleDateNavPrevious}
        onUnitChange={handleUnitChange}
        onFilter={showFilters}
        onExport={handleExport}
      />

      <FiltersBar params={params} onFilterChange={handleFilterChange} />

      <Schedule>
        <Grid
          canvasRef={canvasRef}
          bodyRef={bodyRef}
          start={start}
          end={end}
          unit={params.unit}
          rows={rows}
          styles={styles}
          loading={query.status !== 'ready'}
          navigation={true}
          Sidebar={() => (
            <Sidebar
              groups={groups}
              toggleGroup={toggleGroup}
              toggleGroups={toggleGroups}
              hasExpandedGroups={hasExpandedGroups}
              styles={styles}
              rows={rows}
              components={components}
              parentRef={bodyRef}
            />
          )}
          onDateChange={handleDateChange}>
          {query.status === 'ready' && rows.length > 0 && (
            <>
              <Cells
                bodyRef={bodyRef}
                styles={styles}
                metric={params.metric}
                cells={cells}
                rows={rows}
                start={start}
                end={end}
                unit={params.unit}
                onSelectEnd={handleSelectCellsEnd}
              />

              <Allocations
                allocations={allocations}
                rows={rows}
                dropRows={dropRows}
                styles={styles}
                canvasRef={canvasRef}
                parentRef={bodyRef}
                onView={handleView}
                onEdit={handleEdit}
                onClone={handleClone}
                onSplit={handleSplit}
                onSplitByDay={handleSplitByDay}
                onSplitByWeek={handleSplitByWeek}
                onSplitByMonth={handleSplitByMonth}
                onDelete={handleDelete}
                onAllocationDatesChange={handleUpdateAllocationDates}
                onAllocationDrop={handleAllocationDrop}
              />
            </>
          )}
        </Grid>
      </Schedule>

      <Filters values={params} isOpen={filtersVisible} onApply={handleApplyFilters} onClose={hideFilters} />

      <Switch>
        {auth.allocations.manage && (
          <Route path={[route.path.concat('/new'), route.path.concat('/edit/:allocationId')]}>
            <AllocationForm
              initialValues={allocationFormInitialValues}
              onSaved={handleAllocationSaved}
              onDeleted={fetchAllocations}
              onClose={handleFormClose}
            />
          </Route>
        )}

        <Route path={route.path.concat('/view/:allocationId')}>
          <AllocationDetails onClose={handleFormClose} />
        </Route>

        <Redirect to={route.url.concat(location.search)} />
      </Switch>
    </>
  );
}
